import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faCheckCircle, faTimes, faExclamationTriangle,
  faBullhorn, faEnvelope, faStar, faRocket,
} from '@fortawesome/free-solid-svg-icons';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { useAuth } from '../../context/AuthContext';
import {
  GET_MY_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATION_COUNT,
} from '../../graphql/queries';
import {
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
} from '../../graphql/mutations';

const TYPE_META: Record<string, { icon: any; color: string; bg: string }> = {
  ad_approved:   { icon: faCheckCircle,       color: 'text-success', bg: 'bg-success bg-opacity-10' },
  ad_rejected:   { icon: faTimes,             color: 'text-danger',  bg: 'bg-danger bg-opacity-10'  },
  ad_expired:    { icon: faExclamationTriangle, color: 'text-warning', bg: 'bg-warning bg-opacity-10' },
  boost_approved:{ icon: faRocket,            color: 'text-primary', bg: 'bg-primary bg-opacity-10' },
  boost_rejected:{ icon: faTimes,             color: 'text-danger',  bg: 'bg-danger bg-opacity-10'  },
  new_contact:   { icon: faEnvelope,          color: 'text-info',    bg: 'bg-info bg-opacity-10'    },
  new_rating:    { icon: faStar,              color: 'text-warning', bg: 'bg-warning bg-opacity-10' },
  new_message:   { icon: faEnvelope,          color: 'text-primary', bg: 'bg-primary bg-opacity-10' },
  system:        { icon: faBullhorn,          color: 'text-secondary',bg: 'bg-secondary bg-opacity-10' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const MobileAlerts: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const { data, loading, refetch } = useQuery(GET_MY_NOTIFICATIONS, {
    variables: { limit: 50 },
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  const { data: countData } = useQuery(GET_UNREAD_NOTIFICATION_COUNT, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  const [markRead] = useMutation(MARK_NOTIFICATION_READ, {
    onCompleted: () => refetch(),
  });

  const [markAllRead] = useMutation(MARK_ALL_NOTIFICATIONS_READ, {
    onCompleted: () => refetch(),
  });

  const notifications: any[] = data?.myNotifications || [];
  const unreadCount: number = countData?.unreadNotificationCount || 0;

  const handleNotificationClick = (n: any) => {
    if (!n.isRead) {
      markRead({ variables: { notificationId: n.id } });
    }
    if (n.link) navigate(n.link);
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div className="px-4 py-4 text-white pw-gradient-hero">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h5 fw-bold mb-0">Notifications</h1>
            {unreadCount > 0 && (
              <span className="badge bg-danger mt-1">{unreadCount} unread</span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => markAllRead()}
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="p-3 pw-page">
        {!isAuthenticated ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faBell} className="text-muted fs-1 mb-3" />
            <p className="text-muted">Log in to see your notifications.</p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>Log In</button>
          </div>
        ) : loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faBell} className="text-muted fs-1 mb-3" />
            <p className="text-muted fw-semibold">You're all caught up!</p>
            <p className="text-muted small">No notifications yet.</p>
          </div>
        ) : (
          <div className="list-group list-group-flush rounded-3 overflow-hidden pw-card">
            {notifications.map((n: any, idx: number) => {
              const meta = TYPE_META[n.notificationType] || TYPE_META.system;
              return (
                <motion.button
                  key={n.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className={`list-group-item list-group-item-action d-flex align-items-start gap-3 py-3 px-3 border-0 border-bottom ${
                    !n.isRead ? 'bg-light' : 'bg-white'
                  }`}
                  onClick={() => handleNotificationClick(n)}
                  style={{ textAlign: 'left' }}
                >
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${meta.bg}`}
                    style={{ width: 40, height: 40 }}
                  >
                    <FontAwesomeIcon icon={meta.icon} className={meta.color} />
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex justify-content-between align-items-start">
                      <p className={`mb-0 small ${!n.isRead ? 'fw-semibold' : ''}`}>{n.title}</p>
                      {!n.isRead && (
                        <span className="badge bg-primary ms-2 flex-shrink-0" style={{ fontSize: 8 }}>NEW</span>
                      )}
                    </div>
                    {n.body && <p className="mb-0 small text-muted mt-1">{n.body}</p>}
                    <p className="mb-0 text-muted mt-1" style={{ fontSize: 11 }}>{timeAgo(n.createdAt)}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileAlerts;
